import { Fragment, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { DS_TAB_CHROME } from 'designSystem/config';
import { useTheme } from 'styled-components';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DsTab } from 'redux/mode/reducers';
import { removeDsTab, setActiveDsTab, setDsTabs, showContextMenu } from 'redux/mode/actions';
import * as S from '../styles';

const DS_TAB_DRAG_TYPE = 'DS_TAB';

interface DragItem {
	id: string;
	index: number;
}

interface DraggableTabProps {
	tab: DsTab;
	index: number;
	moveTab: (dragIndex: number, hoverIndex: number) => void;
}

const DraggableTab = ({ tab, index, moveTab }: DraggableTabProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const ref = useRef<HTMLButtonElement>(null);

	const { activeDsTabId, dsTabs } = useSelector(
		(r: RootReducer) => ({
			activeDsTabId: r.mode.activeDsTabId,
			dsTabs: r.mode.dsTabs,
		}),
		shallowEqual
	);

	const [{ isDragging }, drag] = useDrag({
		type: DS_TAB_DRAG_TYPE,
		item: (): DragItem => ({ id: tab.id, index }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [, drop] = useDrop<DragItem>({
		accept: DS_TAB_DRAG_TYPE,
		hover: (item) => {
			if (item.index === index) return;
			moveTab(item.index, index);
			item.index = index;
		},
	});

	drag(drop(ref));

	const chrome = DS_TAB_CHROME[tab.section];
	const tabIcon = chrome?.icon;
	const tabTooltipId = `ds-tab-${tab.id}`;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onTabClose = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		dispatch(removeDsTab(tab.id));
	};

	const onTabContextMenu = (e: React.MouseEvent) => {
		const clickedTabIndex = dsTabs.findIndex((t) => t.id === tab.id);
		if (clickedTabIndex === -1) return;

		const menuItems = [
			new MenuItemAction('Close', '', () => {
				dispatch(removeDsTab(tab.id));
			}),
			new MenuItemDivider(),
			new MenuItemAction(
				'Close Others',
				'',
				() => {
					const remainingTabs = dsTabs.filter((t) => t.id === tab.id);
					dispatch(setDsTabs(remainingTabs));
				},
				dsTabs.length <= 1
			),
			new MenuItemAction(
				'Close to the Right',
				'',
				() => {
					const remainingTabs = dsTabs.slice(0, clickedTabIndex + 1);
					dispatch(setDsTabs(remainingTabs));
				},
				clickedTabIndex >= dsTabs.length - 1
			),
			new MenuItemAction(
				'Close to the Left',
				'',
				() => {
					const remainingTabs = dsTabs.slice(clickedTabIndex);
					dispatch(setDsTabs(remainingTabs));
				},
				clickedTabIndex === 0
			),
		];

		dispatch(
			showContextMenu({
				point: { x: e.clientX, y: e.clientY },
				menuItems,
			})
		);
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<Fragment>
			<S.Tab
				ref={ref}
				$active={tab.id === activeDsTabId}
				$isDragging={isDragging}
				onClick={() => dispatch(setActiveDsTab(tab.id))}
				onContextMenu={onTabContextMenu}
				data-tooltip-id={tabTooltipId}
				data-tooltip-content={tab.label}
			>
				{tabIcon && (
					<S.TabIcon>
						<Icon type={tabIcon} width={14} height={14} color={theme.gray[900]} />
					</S.TabIcon>
				)}
				<S.TabLabel>{tab.label}</S.TabLabel>
				<S.TabCloseBacking />
				<S.TabCloseButton
					onClick={onTabClose}
					tabIndex={0}
					aria-label="Close tab"
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onTabClose(e);
						}
					}}
				>
					<span aria-hidden>
						<Icon type={IconType.CLOSE} width={12} height={12} />
					</span>
				</S.TabCloseButton>
			</S.Tab>
			<Tooltip id={tabTooltipId} placement="bottom" />
		</Fragment>
	);
};

export default DraggableTab;
